import { getLookupMap } from "@citrine/utils/helpers";

import type { AuditInfo, EntityMetadata } from "../common";
import type { IFormulationConstraint } from "../constraint";
import { consolidateFormulationConstraints } from "../constraint";
import type { CSVData, TableData, TrainingData } from "../data-source";
import { isCSVData, isTableData } from "../data-source";
import type {
  ICategoricalDescriptor,
  IDescriptor,
  IFormulationDescriptor,
  IIntegerDescriptor,
  IRealDescriptor,
} from "../descriptor";
import {
  getDescriptorKey,
  isCategoricalDescriptor,
  isRealDescriptor,
  transformDescriptor,
} from "../descriptor";
import type { DesignSpaceSummary } from "../design-space-summary";
import { transformTrainingData } from "../predictor";
import type { ExplicitType } from "../utils";
import { isOfType } from "../utils";

export interface ContinuousDimension {
  descriptor: IRealDescriptor;
  lower_bound: number;
  upper_bound: number;
  type: "ContinuousDimension";
}

export interface IntegerDimension {
  descriptor: IIntegerDescriptor;
  lower_bound: number;
  upper_bound: number;
  type: "IntegerDimension";
}

export interface EnumeratedDimension<
  Descriptor extends IDescriptor = IDescriptor,
> {
  descriptor: Descriptor;
  list: string[];
  type: "EnumeratedDimension";
}

export const isContinuousDimension = isOfType<ContinuousDimension>(
  "ContinuousDimension"
);
export const isEnumeratedDimension = isOfType<EnumeratedDimension>(
  "EnumeratedDimension"
);
export const isIntegerDimension =
  isOfType<IntegerDimension>("IntegerDimension");
export const isEnumeratedDimensionWithRealDescriptor = (
  dimension: Dimension
): dimension is EnumeratedDimension<IRealDescriptor> =>
  isEnumeratedDimension(dimension) && isRealDescriptor(dimension.descriptor);

export const isEnumeratedDimensionWithCategoricalDescriptor = (
  dimension: Dimension
): dimension is EnumeratedDimension<ICategoricalDescriptor> =>
  isEnumeratedDimension(dimension) &&
  isCategoricalDescriptor(dimension.descriptor);

export type Dimension =
  | ContinuousDimension
  | EnumeratedDimension
  | IntegerDimension;

interface DesignSpaceData<
  Config extends IDesignSpaceConfig = IDesignSpaceConfig,
> {
  description: string;
  instance: Config;
  name: string;
}

export type DesignSpaceMetadata = EntityMetadata & {
  locked?: AuditInfo;
  settings?: DesignSpaceSettings;
};

export type DefaultDesignSpaceMode = "ATTRIBUTE" | "HIERARCHICAL";

export interface DesignSpaceSettings {
  mode: DefaultDesignSpaceMode;
  predictor_id: string;
  predictor_version?: number;
  include_ingredient_fraction_constraints?: boolean;
  include_label_fraction_constraints?: boolean;
  include_label_count_constraints?: boolean;
  include_parameter_constraints?: boolean;
  exclude_intermediates?: boolean;
}

export interface DesignSpaceEntity<
  Config extends IDesignSpaceConfig = IDesignSpaceConfig,
> {
  id: string;
  data: DesignSpaceData<Config>;
  metadata: DesignSpaceMetadata;
}

export const isLocked = (ds?: DesignSpaceEntity | DesignSpaceSummary) =>
  Boolean(ds?.metadata.locked);

interface IBaseDesignSpaceConfig<T extends string> extends ExplicitType<T> {
  name: string;
  description: string;
}

export interface IDataSourceDesignSpaceConfig<
  DataSource extends TrainingData = TrainingData,
> extends IBaseDesignSpaceConfig<"DataSourceDesignSpace"> {
  // data_source will always be defined on a persisted record
  // this is optional for newly created objects in the app
  data_source?: DataSource;
  descriptors?: IDescriptor[] | null;
}

export interface IEnumeratedDesignSpaceConfig extends IBaseDesignSpaceConfig<"EnumeratedDesignSpace"> {
  data: Partial<Record<string, string | number>>[];
  descriptors: IDescriptor[];
}

export interface IFormulationDesignSpaceConfig extends IBaseDesignSpaceConfig<"FormulationDesignSpace"> {
  constraints?: IFormulationConstraint[];
  formulation_descriptor: IFormulationDescriptor;
  ingredients?: string[];
  untested_ingredients?: string[];
  labels?: Partial<Record<string, string[]>>;
  resolution?: number;
}

export interface IProductDesignSpaceConfig extends IBaseDesignSpaceConfig<"ProductDesignSpace"> {
  dimensions?: Dimension[];
  subspaces?: Exclude<
    IDesignSpaceConfig,
    IProductDesignSpaceConfig | HierarchicalDesignSpaceConfig
  >[];
}

interface ExternalId {
  id: string;
  scope?: string | null;
  type?: string;
}

export interface TemplateLink {
  material_template: string;
  material_template_name?: string;
  process_template: string;
  process_template_name?: string;
}

export interface MaterialNodeDefinition {
  identifier: ExternalId;
  attributes?: Dimension[];
  formulation?: IFormulationDesignSpaceConfig;
  template?: TemplateLink | null;
  hasError?: boolean;
  display_name?: string;
}

export interface HierarchicalDesignSpaceConfig extends IBaseDesignSpaceConfig<"HierarchicalDesignSpace"> {
  root: MaterialNodeDefinition;
  subspaces: MaterialNodeDefinition[];
}

export type IDesignSpaceConfig =
  | IDataSourceDesignSpaceConfig
  | IEnumeratedDesignSpaceConfig
  | IFormulationDesignSpaceConfig
  | IProductDesignSpaceConfig
  | HierarchicalDesignSpaceConfig;

export type HierarchicalNodeConnections = Record<
  string,
  { incoming: string[]; outgoing: string[] }
>;

export interface LockedReasons {
  ds_id: string;
  is_ds_validating: boolean;
  executions_completed: string[];
  executions_in_progress: string[];
  workflows_in_progress: string[];
}

export type ProductDesignSpaceEditorTypes = "empty" | "formulation" | "other";

export const isDataSourceDesignSpaceConfig =
  isOfType<IDataSourceDesignSpaceConfig>("DataSourceDesignSpace");

export const isHierarchicalDesignSpaceConfig =
  isOfType<HierarchicalDesignSpaceConfig>("HierarchicalDesignSpace");

export const isHierarchicalDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<HierarchicalDesignSpaceConfig> =>
  isHierarchicalDesignSpaceConfig(designSpace?.data.instance);

export const isDataSourceDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IDataSourceDesignSpaceConfig> =>
  isDataSourceDesignSpaceConfig(designSpace?.data.instance);

export const isCSVDataSourceDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IDataSourceDesignSpaceConfig<CSVData>> =>
  isDataSourceDesignSpace(designSpace) &&
  isCSVData(designSpace?.data.instance.data_source);

export const isGEMTableDataSourceDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IDataSourceDesignSpaceConfig<TableData>> =>
  isDataSourceDesignSpace(designSpace) &&
  isTableData(designSpace?.data.instance.data_source);

export const isEnumeratedDesignSpaceConfig =
  isOfType<IEnumeratedDesignSpaceConfig>("EnumeratedDesignSpace");

export const isEnumeratedDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IEnumeratedDesignSpaceConfig> =>
  isEnumeratedDesignSpaceConfig(designSpace?.data.instance);

export const isFormulationDesignSpaceConfig =
  isOfType<IFormulationDesignSpaceConfig>("FormulationDesignSpace");

export const isFormulationDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IFormulationDesignSpaceConfig> =>
  isFormulationDesignSpaceConfig(designSpace?.data.instance);

export const isProductDesignSpaceConfig =
  isOfType<IProductDesignSpaceConfig>("ProductDesignSpace");

export const isProductDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IProductDesignSpaceConfig> =>
  isProductDesignSpaceConfig(designSpace?.data.instance);

export const isFormulationProductDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IProductDesignSpaceConfig> =>
  isFormulationProductDesignSpaceConfig(designSpace?.data.instance);

export const isFormulationProductDesignSpaceConfig = (
  config?: IDesignSpaceConfig
): config is IProductDesignSpaceConfig =>
  isProductDesignSpaceConfig(config) &&
  Boolean(
    config.subspaces?.length === 1 &&
    isFormulationDesignSpaceConfig(config.subspaces[0])
  );

export const isEmptyProductDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IProductDesignSpaceConfig> =>
  isEmptyProductDesignSpaceConfig(designSpace?.data.instance);

export const isEmptyProductDesignSpaceConfig = (
  config?: IDesignSpaceConfig
): config is IProductDesignSpaceConfig =>
  isProductDesignSpaceConfig(config) && config.subspaces?.length === 0;

export const isDataSourceProductDesignSpace = (
  designSpace?: DesignSpaceEntity
): designSpace is DesignSpaceEntity<IProductDesignSpaceConfig> =>
  isProductDesignSpace(designSpace) &&
  Boolean(
    designSpace?.data.instance.subspaces?.length === 1 &&
    isDataSourceDesignSpaceConfig(designSpace.data.instance.subspaces[0])
  );

const getDescriptorsFromDesignSpaceConfig = (
  config: IDesignSpaceConfig
): IDescriptor[] => {
  switch (config.type) {
    case "DataSourceDesignSpace":
    case "EnumeratedDesignSpace":
      return config.descriptors ?? [];

    case "FormulationDesignSpace":
      return [config.formulation_descriptor];

    case "ProductDesignSpace":
      return [
        ...(config.dimensions?.map((dimension) => dimension.descriptor) ?? []),
        ...(Object.values(
          getLookupMap(
            ([] as IDescriptor[]).concat(
              ...(config.subspaces?.map((instance) =>
                getDescriptorsFromDesignSpaceConfig(instance)
              ) ?? [])
            ),
            getDescriptorKey
          )
        ) as IDescriptor[]),
      ];
    case "HierarchicalDesignSpace":
      return [];
  }
};

export const getDescriptorsFromDesignSpace = (
  designSpace: DesignSpaceEntity
): IDescriptor[] =>
  getDescriptorsFromDesignSpaceConfig(designSpace.data.instance);

/**
 * Consolidates formulation constraints for Formulation and Product design space
 * configs. See consolidation function docstring for more information.
 *
 * @param config design space config
 */
const ensureFormulationConstraintsConsolidated = (
  config: IDesignSpaceConfig
) => {
  if (isFormulationDesignSpaceConfig(config)) {
    config.constraints = consolidateFormulationConstraints(
      config.constraints || []
    );
  } else if (isProductDesignSpaceConfig(config)) {
    config.subspaces
      ?.map((subspace) => subspace)
      .forEach(ensureFormulationConstraintsConsolidated);
  }
};

export function transformDesignSpaceConfig(
  config:
    | IDesignSpaceConfig
    | (Omit<IProductDesignSpaceConfig, "type"> & {
        type: "ProductDesignSpace" | "Univariate";
      })
): void {
  switch (config.type) {
    case "DataSourceDesignSpace":
      config.descriptors?.forEach(transformDescriptor);
      transformTrainingData(config.data_source);
      return;

    case "EnumeratedDesignSpace":
      config.descriptors.forEach(transformDescriptor);
      return;

    case "FormulationDesignSpace":
      transformDescriptor(config.formulation_descriptor);
      ensureFormulationConstraintsConsolidated(config);
      return;

    case "ProductDesignSpace":
    // if a design space is reported as Univariate, treat it as a PDS
    case "Univariate":
      config.dimensions
        ?.map(({ descriptor }) => descriptor)
        .forEach(transformDescriptor);
      config.subspaces?.forEach((instance) =>
        transformDesignSpaceConfig(instance)
      );
      config.type = "ProductDesignSpace";
      return;
  }
}

export function isValidLabels(config: HierarchicalDesignSpaceConfig) {
  return [config.root, ...config.subspaces].every((node) =>
    isValidLabelsNode(node)
  );
}

function isValidLabelsNode(node: MaterialNodeDefinition) {
  if (node.formulation) {
    const labelIngredients = Object.values(
      node.formulation.labels ?? {}
    ).flat() as string[];

    // if all ingredients are uniquely labeled, the flattened list of labeled ingredients will be a unique set
    return labelIngredients.length === new Set(labelIngredients).size;
  }
  return true;
}
